import React from "react";
import {
  Card,
  H1,
  H3,
  Text,
  Button,
  ControlGroup,
  HTMLSelect,
  Tag,
  Spinner,
  NonIdealState,
  Icon,
  InputGroup,
  ButtonGroup,
  Divider,
} from "@blueprintjs/core";
import { niceDateTime } from "../../../micro/fns/util";
import { DateInput } from "@blueprintjs/datetime";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallGet, apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import { withRouter } from "react-router-dom";
import {
  getStatusMeta as getStatusMetaGlobal,
  getDisplayStatusMeta as getDisplayStatusMetaGlobal,
} from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

// Set AG Grid license
LicenseManager.setLicenseKey("");

class TPAPaymentPanel extends React.Component {
  state = {
    // Data
    tpaReports: [],
    allReports: [], // Store all data for filtering
    branches: [],

    // Loading states
    isLoading: false,
    isGenerating: false,

    // Filters
    fromDate: new Date(),
    toDate: new Date(),
    selectedBranch: "",
    selectedBranchName: null,
    selectedStatus: "",
    selectedStatusName: null,

    // AG Grid state
    gridApi: null,
    gridColumnApi: null,
    quickFilterText: "",

    // Summary data
    summaryStats: {
      totalRecords: 0,
      financial: {
        totalAuthorized: 0,
        totalUtilized: 0,
        totalCollected: 0,
        totalPending: 0,
        utilizationRate: "0.00",
        collectionRate: "0.00",
      },
      status: {
        overUtilizedCount: 0,
        fullyPaidCount: 0,
        pendingApprovalCount: 0,
        overUtilizationRate: "0.00",
      },
    },
  };

  componentDidMount = async () => {
    await this.loadInitialData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.authStore?.selectedBranchName !== this.props.authStore?.selectedBranchName) {
      const preferredName = this.props.authStore?.selectedBranchName;
      if (!preferredName) {
        this.setState({
          selectedBranch: "",
          selectedBranchName: null,
        });
        return;
      }
      const match = (this.state.branches || []).find(
        (b) => (b.name || "").toLowerCase() === String(preferredName).toLowerCase()
      );
      if (match && match._id !== this.state.selectedBranch) {
        this.setState({
          selectedBranch: match._id,
          selectedBranchName: match.name,
        });
      }
    }
  }

  loadInitialData = async () => {
    try {
      this.setState({ isLoading: true });
      const branches = await this.loadBranches();

      // Set default branch from global selection if available
      if (branches && branches.length > 0) {
        const preferredName = this.props.authStore?.selectedBranchName;
        const norm = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();
        const match = preferredName ? branches.find((b) => norm(b.name) === norm(preferredName)) : null;
        this.setState({
          selectedBranch: (match && match._id) || "",
          selectedBranchName: (match && match.name) || null,
        });
      } else {
        cogoToast.warn("No branches available. Please contact administrator.");
      }

      console.log("🔍 DEBUG: Initial data loaded - Branches:", branches?.length);

      // Auto-generate on initial load
      await this.generateReport();

      // Show success message for loaded filter options
      if (branches?.length > 0) {
        cogoToast.success(`Filter options loaded: ${branches?.length || 0} branches`);
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      cogoToast.error("Failed to load initial data");
      this.setState({ tpaReports: [] });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      // Use the existing branch master API
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});

      // Normalize different response structures to an array of {_id, name}
      const rawBranches = response?.data || response || [];
      const branches = (Array.isArray(rawBranches) ? rawBranches : [])
        .map((site, idx) => ({
          _id:
            site?._id ||
            site?.id ||
            site?.siteId ||
            site?.value ||
            site?.key ||
            (typeof site === "string" ? site : String(idx)),
          name:
            site?.site ||
            site?.siteName ||
            site?.name ||
            site?.branchName ||
            site?.label ||
            (typeof site === "string" ? site : `Site ${idx + 1}`),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      console.log("🔍 DEBUG: Branches loaded:", branches.length);
      this.setState({ branches });
      return branches;
    } catch (err) {
      console.error("Error loading branches:", err);
      // Return dummy branches if API fails
      const dummyBranches = [
        { _id: "branch1", name: "Barrackpore" },
        { _id: "branch2", name: "Kolkata" },
        { _id: "branch3", name: "Howrah" },
        { _id: "branch4", name: "Dum Dum" },
      ];
      this.setState({ branches: dummyBranches });
      return dummyBranches;
    }
  };

  reportPageLimit = 1000;

  isTPAEligibleBooking = (item) => {
    const payorPanelValue =
      item?.payorPanel || item?.payor || item?.patientPanel || item?.patientOriginalData?.panel || "";
    const normalizedPayorPanel = String(payorPanelValue).trim().toLowerCase();

    const hasNonCashPayorPanel =
      normalizedPayorPanel !== "" && normalizedPayorPanel !== "cash" && normalizedPayorPanel !== "self";

    const authorizationEntries = item?.authorization?.entries;
    const hasAuthorizationEntries = Array.isArray(authorizationEntries) && authorizationEntries.length > 0;

    const latestAuth = this.getLatestAuthorization(item);
    const hasAuthorizationDetails = Boolean(
      latestAuth?.tpaName || latestAuth?.insuranceName || latestAuth?.ccn || latestAuth?.claimPayor
    );

    return hasNonCashPayorPanel || hasAuthorizationEntries || hasAuthorizationDetails;
  };

  fetchAllReportRows = async (endpoint, basePayload) => {
    const allRows = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await apiCallPost(endpoint, {
        ...basePayload,
        page,
        limit: this.reportPageLimit,
      });

      const pageItems = response?.data?.items || [];
      const rows = Array.isArray(pageItems) ? pageItems : [];
      allRows.push(...rows);

      const parsedPages = Number(response?.data?.pagination?.pages || 1);
      totalPages = Number.isFinite(parsedPages) && parsedPages > 0 ? parsedPages : 1;

      console.log("🔍 DEBUG: Fetched report page:", {
        page,
        totalPages,
        pageRows: rows.length,
        accumulatedRows: allRows.length,
      });

      page += 1;
    } while (page <= totalPages);

    // Deduplicate rows by booking id in case records shift between page fetches.
    const uniqueRowsById = new Map();
    allRows.forEach((row, idx) => {
      const rowKey = row?._id || row?.id || row?.bookingReference || `row-${idx}`;
      if (!uniqueRowsById.has(rowKey)) {
        uniqueRowsById.set(rowKey, row);
      }
    });

    return Array.from(uniqueRowsById.values());
  };

  generateReport = async () => {
    try {
      this.setState({ isGenerating: true });

      // Request TPA-only data and fetch every page from the API.
      const payload = {
        reportType: "TPA",
        startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
        endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
      };

      // Add branch filter only if a specific branch is selected
      if (this.state.selectedBranch) {
        // API expects branchName (actual name), not branch ID
        const selectedBranchObj = this.state.branches.find((b) => b._id === this.state.selectedBranch);
        const branchName = selectedBranchObj?.name;
        if (branchName) {
          payload.branch = branchName; // Send actual branch name, not ID
        }
      }

      console.log("🔍 DEBUG: Branch mapping:", {
        selectedBranchId: this.state.selectedBranch,
        selectedBranchObj: this.state.selectedBranch
          ? this.state.branches.find((b) => b._id === this.state.selectedBranch)
          : null,
        branchNameSent: payload.branch || "All Branches",
      });

      // Use same endpoint as IPDBillRegister
      const endpoint = `/v3/${this.props.authStore.prefix}/reports/ipdBillRegister`;
      console.log("🔍 DEBUG: POST Endpoint:", endpoint, "Payload:", payload);
      const rows = await this.fetchAllReportRows(endpoint, payload);

      console.log("🔍 DEBUG: Extracted rows (all pages):", rows.length, rows.slice(0, 2));

      // Keep a defensive filter so rows with only payor panel are still included.
      const tpaRows = rows.filter((item) => this.isTPAEligibleBooking(item));

      console.log("🔍 DEBUG: Filtered TPA rows:", tpaRows.length);

      // Map to display schema and store all reports
      const mapped = tpaRows.map((item) => this.mapItemToRow(item));

      console.log("🔍 DEBUG: Mapped data:", mapped.length, mapped.slice(0, 2));

      // Store all data first
      this.setState({
        allReports: mapped,
      });

      // Apply status filter if one is selected
      let filteredData = mapped;
      if (this.state.selectedStatus) {
        filteredData = mapped.filter((item) => {
          const itemStatus = item._statusRaw;
          const normalizedItemStatus = String(itemStatus || "").toUpperCase();
          return normalizedItemStatus === this.state.selectedStatus;
        });
      }

      this.setState({
        tpaReports: filteredData,
      });

      if (mapped.length > 0) {
        const statusFilterText = this.state.selectedStatus ? ` (${this.state.selectedStatusName} only)` : "";
        cogoToast.success(
          `TPA Report generated • ${filteredData.length} displayed of ${mapped.length} total records${statusFilterText}`
        );
      } else {
        cogoToast.warn("No TPA/Insurance records found for the selected criteria");
      }
    } catch (err) {
      console.error("Error generating TPA Payment Panel report:", err);

      // Handle different types of API errors
      if (err?.response?.status === 304) {
        // 304 means data hasn't changed - use existing data
        console.log("🔍 DEBUG: 304 Not Modified - filtering existing data");
        if (this.state.allReports && this.state.allReports.length > 0) {
          cogoToast.success(`Using cached data • ${this.state.allReports.length} total records`);
        } else {
          cogoToast.warn("Data not modified since last request, but no cached data available.");
        }
      } else if (err?.response?.status === 404) {
        cogoToast.error("Report endpoint not found. Please contact administrator.");
        this.setState({ tpaReports: [], allReports: [] });
      } else if (err?.response?.status >= 500) {
        cogoToast.error("Server error occurred. Please try again later.");
        this.setState({ tpaReports: [], allReports: [] });
      } else {
        cogoToast.error(
          `Failed to generate report: ${err?.response?.data?.message || err?.message || "Unknown error"}`
        );
        this.setState({ tpaReports: [], allReports: [] });
      }
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date });
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value });
  };

  // Helper function to create branch items for dropdown
  createBranchSelectItems = () => {
    return this.state.branches.map((branch) => branch.name).filter(Boolean);
  };

  // Handle branch selection from SingleSelectWrapper
  handleBranchFilterSelect = (branchName) => {
    const selectedBranch = this.state.branches.find((branch) => branch.name === branchName);
    this.setState({
      selectedBranch: selectedBranch ? selectedBranch._id : "",
      selectedBranchName: branchName || null,
    });
  };

  // Helper function to create status items for dropdown
  createStatusSelectItems = () => {
    return ["Confirmed", "Discharged", "Cancelled"];
  };

  // Handle status selection from SingleSelectWrapper
  handleStatusFilterSelect = (statusName) => {
    let statusValue = "";
    if (statusName === "Confirmed") {
      statusValue = "CONFIRMED";
    } else if (statusName === "Discharged") {
      statusValue = "DISCHARGED";
    } else if (statusName === "Cancelled") {
      statusValue = "CANCELLED";
    }

    this.setState({
      selectedStatus: statusValue,
      selectedStatusName: statusName || null,
    });

    // Apply the status filter immediately
    this.applyStatusFilter(statusValue);
  };

  // Apply status filter to the data
  applyStatusFilter = (statusValue) => {
    let filteredData = [...this.state.allReports];

    // Apply status filter if selected
    if (statusValue) {
      filteredData = filteredData.filter((item) => {
        const itemStatus = item._statusRaw;
        const normalizedItemStatus = String(itemStatus || "").toUpperCase();
        return normalizedItemStatus === statusValue;
      });
    }

    this.setState({ tpaReports: filteredData });

    if (this.state.gridApi) {
      this.state.gridApi.setRowData(filteredData);
    }
  };

  clearFilters = () => {
    this.setState({
      selectedBranch: "",
      selectedBranchName: null,
      selectedStatus: "",
      selectedStatusName: null,
      fromDate: new Date(),
      toDate: new Date(),
      quickFilterText: "",
    });

    // Reset data to show all records
    this.setState({ tpaReports: this.state.allReports });
    if (this.state.gridApi) {
      this.state.gridApi.setRowData(this.state.allReports);
      this.state.gridApi.setQuickFilter("");
    }

    cogoToast.success("Filters cleared");
  };

  hasActiveFilters = () => {
    const { selectedBranch, selectedStatus, fromDate, toDate } = this.state;
    const today = moment().format("YYYY-MM-DD");
    const defaultFromDate = today;
    const fromDateStr = moment(fromDate).format("YYYY-MM-DD");
    const toDateStr = moment(toDate).format("YYYY-MM-DD");

    return selectedBranch !== "" || selectedStatus !== "" || fromDateStr !== defaultFromDate || toDateStr !== today;
  };

  formatCurrency = (num) => {
    if (num === null || typeof num === "undefined") return "-";
    try {
      return num.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
    } catch (e) {
      return `₹${num}`;
    }
  };

  formatPercentage = (num) => {
    if (num === null || typeof num === "undefined") return "-";
    return `${Number(num).toFixed(2)}%`;
  };

  // Helper functions from IPDBillRegister
  getNumber = (...candidates) => {
    for (const c of candidates) {
      if (typeof c === "number" && !Number.isNaN(c)) return c;
      if (typeof c === "string" && c.trim() !== "" && !Number.isNaN(Number(c))) return Number(c);
    }
    return 0;
  };

  // Helper function to safely convert paise to rupees
  paiseToRupees = (paise) => {
    const num = this.getNumber(paise);
    return num / 100;
  };

  // Helper function to get latest authorization
  getLatestAuthorization = (item) => {
    const entries = item?.authorization?.entries || [];
    if (!Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    const activeIndex = item?.authorization?.activeIndex;
    if (typeof activeIndex === "number" && activeIndex >= 0 && activeIndex < entries.length) {
      return entries[activeIndex];
    }

    // If no active index, return the most recent entry
    return entries.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))[0];
  };

  // Helper function to get latest invoice
  getLatestInvoice = (item) => {
    const invoices = item?.invoices || [];
    if (!Array.isArray(invoices) || invoices.length === 0) {
      return null;
    }

    // Return the most recent invoice
    return invoices.sort((a, b) => new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0))[0];
  };

  // Helper function to format payment events from latest invoice
  formatPaymentCollection = (item) => {
    const latestInvoice = this.getLatestInvoice(item);
    const payments = latestInvoice?.payments || item?.payments?.events || [];

    if (!Array.isArray(payments) || payments.length === 0) {
      return "-";
    }

    // Sort events by timestamp (newest first)
    const sortedEvents = payments.sort((a, b) => {
      const dateA = new Date(a.ts || 0);
      const dateB = new Date(b.ts || 0);
      return dateB - dateA;
    });

    return sortedEvents
      .map((event) => {
        const amount = (event.amountPaise || event.amount || 0) / (event.amountPaise ? 100 : 1);
        const mode = event.mode || "Unknown";
        const moneyReceiptNo = event.moneyReceiptNo || "No Receipt";
        const date = event.ts ? moment(event.ts).format("DD/MM/YYYY") : "Unknown";
        const prefix = event.type === "Refund" ? "-" : "+";
        const type = event.type || "Payment";

        return `${type}: ${prefix}${amount} | ${mode} | ${moneyReceiptNo} | ${date}`;
      })
      .join("\n");
  };

  formatStatus = (status) => {
    if (!status) return "-";

    // Normalize the status first
    const normalized = String(status).toUpperCase().trim();

    // Map specific statuses to proper display format
    const statusMap = {
      CONFIRMED: "Confirmed",
      DISCHARGED: "Discharged",
      CANCELLED: "Cancelled",
      CANCELED: "Cancelled", // Handle alternative spelling
      SCHEDULED: "Scheduled",
      PENDING: "Pending",
      COMPLETED: "Completed",
      POSTPONED: "Postponed",
      RESCHEDULED: "Rescheduled",
      BILLED: "Billed",
    };

    // Return mapped status or fallback to title case
    return (
      statusMap[normalized] ||
      String(status)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  getStatusMeta = (status) => {
    const meta = getStatusMetaGlobal(status);
    return { ...meta, label: this.formatStatus(status) };
  };

  getDisplayStatusMeta = (row) => {
    const status = row?._statusRaw || row?.Status || "-";
    const payorPanel =
      row?.payorPanel ||
      row?.payor ||
      row?.patientPanel ||
      row?._originalItem?.payorPanel ||
      row?._originalItem?.payor ||
      row?._originalItem?.patientPanel ||
      "";
    const isBilled = row?.isBilled ?? row?._originalItem?.isBilled ?? false;
    return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
  };

  renderStatusCell = (row, fallback) => {
    if (!row && !fallback) {
      return "-";
    }
    const meta = this.getDisplayStatusMeta(row || { Status: fallback });
    return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
  };
  renderIsBilledCell = (row) => getIsBilledPill(row);

  mapItemToRow = (item) => {
    console.log("🔍 DEBUG: Mapping TPA item:", item);

    // Get latest invoice and authorization
    const latestInvoice = this.getLatestInvoice(item);
    const latestAuth = this.getLatestAuthorization(item);
    const consolidatedBilling = latestInvoice?.consolidatedBilling || item?.consolidatedBilling || {};

    // Calculate values based on new API structure
    const surgeryRatePaise = this.getNumber(consolidatedBilling?.surgeryRatePaise);
    const anaestheticChargesPaise = this.getNumber(consolidatedBilling?.anaestheticChargesPaise);
    const tissueProcessingPaise = this.getNumber(consolidatedBilling?.tissueProcessingPaise);
    const femtoChargesPaise = this.getNumber(consolidatedBilling?.femtoChargesPaise);
    const specialDiscountPaise = this.getNumber(consolidatedBilling?.specialDiscountPaise);
    const mouDiscountPaise = this.getNumber(consolidatedBilling?.mouDiscountPaise);
    const iolDiscountPaise = this.getNumber(consolidatedBilling?.iolDiscountPaise);

    // Convert paise to rupees - ensure 0 instead of negative values
    const packages = Math.max(0, (surgeryRatePaise + anaestheticChargesPaise + tissueProcessingPaise) / 100);
    const specialDiscount = Math.max(0, specialDiscountPaise / 100);
    const mouDiscount = Math.max(0, mouDiscountPaise / 100);
    const iolDiscount = Math.max(0, iolDiscountPaise / 100);
    const femtoCharges = Math.max(0, femtoChargesPaise / 100);

    // Calculate total receivable - ensure 0 minimum
    const totalReceivable = Math.max(0, packages + femtoCharges - specialDiscount - mouDiscount - iolDiscount);

    // Get authorization amount - ensure 0 if not available
    const authorization = Math.max(0, this.getNumber(latestAuth?.amountRupees));

    // Calculate patient payable (Net Payable) - ensure 0 minimum
    const netPayable = Math.max(0, totalReceivable - authorization);

    // Get payment totals
    const totalPaid = this.getNumber(item?.payments?.totalPaid, item?.payments?.totalPaidPaise / 100);
    const totalRefunded = this.getNumber(item?.payments?.totalRefunded, item?.payments?.totalRefundedPaise / 100);
    const collected = totalPaid - totalRefunded;

    // Calculate balance
    const balance = netPayable - collected;

    // Get status information
    const statusRaw =
      item?.status || item?.bookingStatus || item?.workflowStatus || latestInvoice?.status || item?.surgeryStatus;
    const payorPanel =
      item?.payorPanel ||
      item?.payor ||
      item?.patientPanel ||
      item?.patientOriginalData?.panel ||
      latestAuth?.payorPanel ||
      "";
    const isBilled = Boolean(item?.isBilled || latestInvoice?.isBilled || statusRaw?.toString().toLowerCase() === "billed");

    const displayStatusMeta = getDisplayStatusMetaGlobal({
      status: statusRaw,
      payorPanel,
      isBilled,
    });
    const statusLabel = displayStatusMeta.label || this.formatStatus(statusRaw);

    // TPA Payment Panel row structure with requested columns
    return {
      _id: item?._id || item?.id || item?.bookingReference || "-",
      "Invoice No": latestInvoice?.invoiceNumber || latestInvoice?.number || "-",
      "Surgery ID": item?.bookingReference || "-",
      PID: item?.patientGenericPid || item?.patientId || item?.patientOriginalData?.sysPid || "-",
      "Patient Name": item?.patientName || item?.patientOriginalData?.name || "-",
      Status: statusLabel,
      "TPA Name": latestAuth?.tpaName || "-",
      "Insurance Name": latestAuth?.insuranceName || "-",
      "CC No": latestAuth?.ccn || "-",
      "Claim Payor": latestAuth?.claimPayor || "-",
      Remarks: latestAuth?.remarks || latestAuth?.notes || "-",
      Packages: packages,
      "Special Discount": specialDiscount,
      "Mou Discount": mouDiscount,
      "Total Receivable": totalReceivable,
      Authorization: authorization,
      "Net Payable": netPayable,
      "Patient Paid": collected,
      Balance: balance,
      "Authorization Recorded By": latestAuth?.uploadedByName || latestAuth?.createdByName || "-",

      // Additional fields for filtering (not displayed but used for filter logic)
      _originalItem: item,
      payorPanel,
      isBilled,
      _branchId: item?.branchId || "",
      _branchName: item?.branchName || "",
      _doctorId: item?.doctorId || "",
      _doctorName: item?.doctorName || "",
      _createdAt: item?.scheduledDate || item?.createdAt,
      _tpaName: latestAuth?.tpaName || "",
      _insuranceName: latestAuth?.insuranceName || "",
      _statusRaw: statusRaw,
    };
  };

  getColumnDefs = () => {
    const currencyFormatter = (params) => {
      const value = Number(params.value || 0);
      return `₹${value.toLocaleString("en-IN")}`;
    };

    return [
      {
        headerName: "Invoice No",
        field: "Invoice No",
        width: 120,
        pinned: "left",
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Surgery ID",
        field: "Surgery ID",
        width: 120,
        pinned: "left",
        cellRenderer: (params) => {
          const value = params.value;
          if (!value || value === "-") return "-";

          const container = document.createElement("div");
          const link = document.createElement("span");

          link.innerHTML = value;
          link.style.color = "#137cbd";
          link.style.cursor = "pointer";
          link.style.textDecoration = "underline";
          link.style.fontWeight = "600";
          link.onclick = () => this.navigateToDetails(params.data);

          container.appendChild(link);
          return container;
        },
      },
      {
        headerName: "PID",
        field: "PID",
        width: 120,
        pinned: "left",
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Patient Name",
        field: "Patient Name",
        width: 180,
        pinned: "left",
      },
      {
        headerName: "Status",
        field: "Status",
        width: 120,
        filter: "agSetColumnFilter",
        filterValueGetter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
        valueFormatter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
        cellRenderer: (params) => this.renderStatusCell(params.data),
        cellStyle: { textAlign: "center", fontWeight: "600" },
      },
      {
        headerName: "Is Billed",
        field: "isBilled",
        width: 120,
        filter: "agSetColumnFilter",
        valueGetter: (params) => getIsBilledLabel(params.data),
        valueFormatter: (params) => getIsBilledLabel(params.data),
        cellRenderer: (params) => this.renderIsBilledCell(params.data),
        cellStyle: { textAlign: "center", fontWeight: "600" },
      },
      {
        headerName: "TPA Name",
        field: "TPA Name",
        width: 150,
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Insurance Name",
        field: "Insurance Name",
        width: 150,
      },
      {
        headerName: "CC No",
        field: "CC No",
        width: 130,
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Claim Payor",
        field: "Claim Payor",
        width: 150,
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Remarks",
        field: "Remarks",
        width: 200,
      },
      {
        headerName: "Packages",
        field: "Packages",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Special Discount",
        field: "Special Discount",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "MOU Discount",
        field: "Mou Discount",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Total Receivable",
        field: "Total Receivable",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Authorization",
        field: "Authorization",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
        cellStyle: { fontWeight: "600" },
      },
      {
        headerName: "Net Payable",
        field: "Net Payable",
        width: 140,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Patient Paid",
        field: "Patient Paid",
        width: 120,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
      },
      {
        headerName: "Balance",
        field: "Balance",
        width: 120,
        type: "numericColumn",
        valueFormatter: currencyFormatter,
        cellRenderer: (params) => {
          const value = params.value;
          let color = "#ffffff";

          if (value > 0) {
            color = "#db3737"; // Red for positive balance (amount owed)
          } else if (value === 0) {
            color = "#0f9960"; // Green for zero balance (fully paid)
          } else {
            color = "#d9822b"; // Orange for negative balance (overpaid)
          }

          const formattedValue = currencyFormatter(params);
          return `<span style="color: ${color}; font-weight: 600;">${formattedValue}</span>`;
        },
      },
      {
        headerName: "Authorization Recorded By",
        field: "Authorization Recorded By",
        width: 180,
      },
    ];
  };

  // Navigate to surgery details page
  navigateToDetails = (surgery) => {
    const detailsPath = `/v3/workflow/surgery-booking/${
      surgery._id || surgery.docNo || surgery.bookingReference || surgery["Surgery ID"]
    }`;
    window.open(detailsPath, "_blank", "noopener,noreferrer");
  };

  // AG Grid event handlers
  onGridReady = (params) => {
    this.setState({
      gridApi: params.api,
      gridColumnApi: params.columnApi,
    });
  };

  // Get only visible/displayed columns (respects sidebar column selection)
  getDisplayedColumns = () => {
    if (!this.state.gridColumnApi) return this.getColumnDefs();
    const allColumns = this.state.gridColumnApi.getAllColumns();
    if (!allColumns) return this.getColumnDefs();
    const visibleColumns = allColumns.filter((col) => col.isVisible());
    return visibleColumns.map((col) => {
      const colDef = col.getColDef();
      return {
        headerName: colDef.headerName,
        field: colDef.field,
        ...colDef,
      };
    });
  };

  getRowsForExport = () => {
    const { gridApi, tpaReports } = this.state;
    if (!gridApi) {
      return tpaReports || [];
    }

    const rows = [];
    gridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node?.data) {
        rows.push(node.data);
      }
    });
    return rows;
  };

  onQuickFilterChanged = (event) => {
    this.setState({ quickFilterText: event.target.value });
  };

  // Helper function to calculate totals for numeric columns
  calculateTotals = (data) => {
    const numericFields = [
      "Packages",
      "Special Discount",
      "Mou Discount",
      "Total Receivable",
      "Authorization",
      "Net Payable",
      "Patient Paid",
      "Balance",
    ];

    const totals = {};

    // Initialize totals
    numericFields.forEach((field) => {
      totals[field] = 0;
    });

    // Calculate sums
    data.forEach((row) => {
      numericFields.forEach((field) => {
        const value = Number(row[field] || 0);
        if (!isNaN(value)) {
          totals[field] += value;
        }
      });
    });

    return totals;
  };

  // Helper function to format currency for totals
  formatCurrencyForTotal = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
  };

  exportToCSV = () => {
    const data = this.getRowsForExport();
    if (!data.length) {
      cogoToast.warn("No records to export");
      return;
    }

    const columns = this.getDisplayedColumns();
    if (!columns || !columns.length) {
      cogoToast.warn("No columns available to export");
      return;
    }

    const totals = this.calculateTotals(data);

    const headers = columns.map((col) => col.headerName).join(",");

    const rows = data
      .map((row) => {
        return columns
          .map((col) => {
            const value = row[col.field];
            if (value === null || typeof value === "undefined") {
              return "";
            }
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(",");
      })
      .join("\n");

    const totalsRow = columns
      .map((col) => {
        const field = col.field;
        if (Object.prototype.hasOwnProperty.call(totals, field)) {
          return totals[field].toLocaleString("en-IN");
        }
        if (field === "Invoice No") {
          return "TOTAL";
        }
        return "";
      })
      .join(",");

    const csvContent = `${headers}\n${rows}\n${totalsRow}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `tpa-payment-panel-${moment().format("YYYYMMDD-HHmm")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    cogoToast.success("CSV exported successfully with totals!");
  };

  exportToPDF = () => {
    const rows = this.getRowsForExport();
    if (!rows.length) {
      cogoToast.warn("No records to export");
      return;
    }

    const originalColumns = this.getDisplayedColumns();
    if (!originalColumns || !originalColumns.length) {
      cogoToast.warn("No columns available to export");
      return;
    }

    const pdfColumns = originalColumns.filter((c) => c.field !== "PID" && c.field !== "Patient Name");

    const pidIndex = originalColumns.findIndex((c) => c.field === "PID");
    if (pidIndex !== -1) {
      pdfColumns.splice(pidIndex, 0, {
        headerName: "PID / Patient Name",
        field: "PID / Patient Name",
      });
    }

    const win = window.open("", "_blank");
    if (!win) {
      cogoToast.error("Popup blocked. Allow popups to export PDF");
      return;
    }

    const styles = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h2 { margin: 0 0 8px 0; }
        .meta { color: #555; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #999; padding: 6px; text-align: left; }
        th { background: #eee; }
        .totals-row { background: #f0f0f0 !important; font-weight: bold !important; }
      </style>
    `;

    const headerHtml = `
      <h2>TPA Payment Panel Report</h2>
      <div class="meta">${moment(this.state.fromDate).format("DD-MMM-YYYY")} to ${moment(this.state.toDate).format(
      "DD-MMM-YYYY"
    )} | ${rows.length} records</div>
    `;

    const thead = `<tr>${pdfColumns.map((c) => `<th>${c.headerName}</th>`).join("")}</tr>`;
    const totals = this.calculateTotals(rows);

    const tbody = rows
      .map(
        (r) =>
          `<tr>${pdfColumns
            .map((c) => {
              let v;
              if (c.field === "PID / Patient Name") {
                const pid = r["PID"] || "-";
                const patientName = r["Patient Name"] || "-";
                v = `${pid} / ${patientName}`;
              } else {
                v = r[c.field];
              }
              return `<td>${v === null || typeof v === "undefined" ? "" : v}</td>`;
            })
            .join("")}</tr>`
      )
      .join("");

    const totalsRow = `<tr style="background: #f0f0f0; font-weight: bold;">${pdfColumns
      .map((c) => {
        const field = c.field;
        if (field === "PID / Patient Name") {
          return `<td style="font-weight: bold;">TOTAL</td>`;
        }
        if (Object.prototype.hasOwnProperty.call(totals, field)) {
          return `<td style="font-weight: bold; text-align: right;">${this.formatCurrencyForTotal(totals[field])}</td>`;
        }
        return `<td></td>`;
      })
      .join("")}</tr>`;

    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"/>${styles}</head><body>${headerHtml}<table>${thead}${tbody}${totalsRow}</table></body></html>`
    );
    win.document.close();
    win.focus();
    win.print();
    cogoToast.success("PDF opened for printing");
  };

  renderSummaryCards = () => {
    const { summaryStats } = this.state;

    return (
      <div style={{ display: "flex", gap: "15px", marginBottom: "20px", flexWrap: "wrap" }}>
        <Card
          elevation={1}
          style={{
            padding: "15px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            minWidth: "200px",
            flex: 1,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: "600" }}>
              TOTAL AUTHORIZED
            </Text>
            <H3 style={{ color: "var(--intent-success)", margin: "5px 0" }}>
              {this.formatCurrency(summaryStats.financial?.totalAuthorized || 0)}
            </H3>
          </div>
        </Card>

        <Card
          elevation={1}
          style={{
            padding: "15px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            minWidth: "200px",
            flex: 1,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: "600" }}>TOTAL UTILIZED</Text>
            <H3 style={{ color: "var(--intent-primary)", margin: "5px 0" }}>
              {this.formatCurrency(summaryStats.financial?.totalUtilized || 0)}
            </H3>
          </div>
        </Card>

        <Card
          elevation={1}
          style={{
            padding: "15px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            minWidth: "200px",
            flex: 1,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: "600" }}>TOTAL COLLECTED</Text>
            <H3 style={{ color: "var(--intent-warning)", margin: "5px 0" }}>
              {this.formatCurrency(summaryStats.financial?.totalCollected || 0)}
            </H3>
          </div>
        </Card>

        <Card
          elevation={1}
          style={{
            padding: "15px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            minWidth: "200px",
            flex: 1,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text style={{ color: "var(--text-secondary)", fontSize: "12px", fontWeight: "600" }}>
              UTILIZATION RATE
            </Text>
            <H3 style={{ color: "var(--intent-danger)", margin: "5px 0" }}>
              {this.formatPercentage(summaryStats.financial?.utilizationRate || 0)}
            </H3>
          </div>
        </Card>
      </div>
    );
  };

  renderFilters = () => {
    const { isLoading } = this.state;

    return (
      <Card
        elevation={2}
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
          <Icon icon="filter" style={{ marginRight: "10px", color: "var(--text-primary)" }} />
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
            {isLoading && (
              <Tag intent="none" large>
                <Spinner size={16} style={{ marginRight: "8px" }} />
                Loading...
              </Tag>
            )}
            <Tag intent="success" large>
              {this.state.tpaReports.length} Records
            </Tag>
          </div>
        </div>

        {/* First row - Date, Branch, and Status filters */}
        <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap", marginBottom: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              From
            </Tag>
            <DateInput
              value={this.state.fromDate}
              onChange={(d) => this.handleDateChange("fromDate", d)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="From Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              To
            </Tag>
            <DateInput
              value={this.state.toDate}
              onChange={(d) => this.handleDateChange("toDate", d)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
              placeholder="To Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Branch
            </Tag>
            <div style={{ width: "160px" }}>
              <SingleSelectWrapper
                items={[`All Branches (${this.state.branches.length})`, ...this.createBranchSelectItems()]}
                selectedItem={this.state.selectedBranchName || `All Branches (${this.state.branches.length})`}
                placeholder="Search branches..."
                onChange={(value) => {
                  if (value === `All Branches (${this.state.branches.length})`) {
                    this.handleBranchFilterSelect(null);
                  } else {
                    this.handleBranchFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Status
            </Tag>
            <div style={{ width: "150px" }}>
              <SingleSelectWrapper
                items={["All Status", ...this.createStatusSelectItems()]}
                selectedItem={this.state.selectedStatusName || "All Status"}
                placeholder="Search status..."
                onChange={(value) => {
                  if (value === "All Status") {
                    this.handleStatusFilterSelect(null);
                  } else {
                    this.handleStatusFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>
        </div>

        {/* Second row - Quick Filter and Actions */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Quick Filter
            </Tag>
            <InputGroup
              placeholder="Search all columns..."
              value={this.state.quickFilterText}
              onChange={this.onQuickFilterChanged}
              leftIcon="search"
              style={{ width: "300px" }}
              small
            />
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <Button intent="primary" icon="search" onClick={this.generateReport} loading={this.state.isGenerating}>
              Generate Report
            </Button>
            <Button icon="filter-remove" onClick={this.clearFilters} disabled={!this.hasActiveFilters()}>
              Clear Filters
            </Button>
            <Button icon="document" onClick={this.exportToPDF} disabled={!this.state.tpaReports.length}>
              PDF
            </Button>
            <Button icon="th" onClick={this.exportToCSV} disabled={!this.state.tpaReports.length}>
              CSV
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  renderTable = () => {
    if (this.state.isGenerating) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Generating TPA Report..."
          description="Please wait while we fetch the TPA panel data"
        />
      );
    }

    if (!this.state.tpaReports || this.state.tpaReports.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-secondary)",
            backgroundColor: "var(--bg-secondary)",
            borderRadius: "8px",
            border: "1px solid var(--border-color)",
          }}
        >
          <Icon icon="credit-card" size={48} style={{ marginBottom: "15px", opacity: 0.5 }} />
          <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>No TPA records found</div>
          <div style={{ fontSize: "14px", opacity: 0.8 }}>Try adjusting your date range or filters</div>
        </div>
      );
    }

    return (
      <div
        className="ag-theme-alpine-dark"
        style={{
          height: "600px",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <AgGridReact
          rowData={this.state.tpaReports}
          columnDefs={this.getColumnDefs()}
          onGridReady={this.onGridReady}
          quickFilterText={this.state.quickFilterText}
          animateRows={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableBrowserTooltips={true}
          enableRangeSelection={true}
          enableCharts={true}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
              },
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
              },
            ],
            defaultToolPanel: null, // No panel open by default
            hiddenByDefault: false,
          }}
          statusBar={{
            statusPanels: [
              {
                statusPanel: "agTotalAndFilteredRowCountComponent",
                align: "left",
              },
              {
                statusPanel: "agSelectedRowCountComponent",
                align: "center",
              },
              {
                statusPanel: "agAggregationComponent",
                align: "right",
              },
            ],
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 80,
            enableRowGroup: true,
            enablePivot: true,
            enableValue: true,
          }}
          sortingOrder={["desc", "asc"]}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
          domLayout="normal"
          getRowId={(params) => params.data._originalItem?.rowId || Math.random()}
          rowHeight={45}
          headerHeight={40}
        />
      </div>
    );
  };

  renderInfoPanel = () => {
    return (
      <Card
        elevation={1}
        style={{
          padding: "15px",
          backgroundColor: "var(--bg-secondary)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <Icon icon="credit-card" style={{ marginRight: "8px", color: "var(--intent-primary)" }} />
          <Text
            style={{
              color: "var(--text-primary)",
              margin: 0,
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            TPA Payment Panel - Comprehensive analysis of TPA authorizations, utilizations, and payments
          </Text>
        </div>
      </Card>
    );
  };

  renderHeader = () => {
    const branchName = this.state.branches.find((b) => b._id === this.state.selectedBranch)?.name || "All Branches";
    return (
      <div
        style={{
          textAlign: "center",
          marginBottom: "20px",
          borderBottom: "2px solid var(--border-color)",
          paddingBottom: "12px",
        }}
      >
        <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
          TPA PAYMENT PANEL - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
        </H1>
        <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "16px" }}>
          {branchName} - {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
          {moment(this.state.toDate).format("DD-MMM-YYYY")}
        </Text>
        <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "14px" }}>
          TPA Authorization & Payment Analysis Report
        </Text>
      </div>
    );
  };

  render() {
    if (this.state.isLoading) {
      return <NonIdealState icon={<Spinner />} title="Loading..." description="Preparing filters and data" />;
    }
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Icon icon="credit-card" style={{ marginRight: "12px", color: "var(--intent-primary)" }} />
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>TPA Payment Panel</H1>
          </div>
        </div>

        {/* {this.renderInfoPanel()} */}
        {/* {this.renderSummaryCards()} */}
        {this.renderFilters()}

        <Card
          elevation={2}
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
          }}
        >
          {this.renderHeader()}
          {this.renderTable()}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(TPAPaymentPanel)));
